/**
 * EsimSuccessPage — Confirms Stripe payment & triggers Airalo provisioning.
 * Reached by Stripe success_url. Polls /confirm until eSIM is active.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, AlertCircle, Loader2, QrCode } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const EsimSuccessPage = () => {
  const { t } = useLanguage();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const orderId = params.get('order_id');
  const isTopup = params.get('topup') === '1';
  const [state, setState] = useState('loading'); // loading | active | failed
  const [order, setOrder] = useState(null);

  const poll = useCallback(async (attempt = 0) => {
    if (!orderId) {
      setState('failed');
      return;
    }
    const endpoint = isTopup
      ? `/api/esim/orders/${orderId}/confirm-topup`
      : `/api/esim/orders/${orderId}/confirm`;
    try {
      const res = await axios.post(endpoint);
      setOrder(res.data);
      const status = res.data?.status;
      const stripeStatus = res.data?.stripe_payment_status;
      if (status === 'active') {
        setState('active');
      } else if (status === 'airalo_failed') {
        setState('failed');
      } else if (status === 'pending_payment' && stripeStatus && stripeStatus !== 'paid') {
        // Stripe says not paid — likely cancelled. Stop polling after a few tries.
        if (attempt >= 2) {
          setState('cancelled');
          return;
        }
        setTimeout(() => poll(attempt + 1), 2000);
      } else if (attempt >= 20) {
        setState('failed');
      } else {
        setTimeout(() => poll(attempt + 1), 1500);
      }
    } catch {
      setState('failed');
    }
  }, [orderId, isTopup]);

  useEffect(() => {
    poll();
  }, [poll]);

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-6" data-testid="esim-success-page">
      <div className="max-w-md w-full bg-gradient-to-br from-zinc-900 to-black rounded-2xl border border-white/10 p-6 text-center">
        {state === 'loading' && (
          <>
            <Loader2 className="w-12 h-12 text-purple-400 mx-auto mb-4 animate-spin" />
            <h2 className="text-xl font-bold text-white mb-2">{t('esim.activating')}</h2>
            <p className="text-white/60 text-sm">{t('esim.paymentSuccess')}</p>
          </>
        )}
        {state === 'active' && (
          <>
            <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">{t('esim.activated')}</h2>
            <p className="text-white/60 text-sm mb-6">{t('esim.installTitle')}</p>
            {order?.qr_url && (
              <div className="bg-white p-3 rounded-xl inline-block mb-4">
                <img src={order.qr_url} alt="QR" className="w-48 h-48" />
              </div>
            )}
            <button
              type="button"
              onClick={() => navigate('/settings')}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2"
              data-testid="esim-success-go-myesims"
            >
              <QrCode className="w-4 h-4" />
              {t('esim.mySectionTitle')}
            </button>
          </>
        )}
        {state === 'failed' && (
          <>
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">{t('esim.paymentFailed')}</h2>
            <button
              type="button"
              onClick={() => navigate('/films')}
              className="mt-4 px-6 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm"
            >
              {t('esim.close')}
            </button>
          </>
        )}
        {state === 'cancelled' && (
          <>
            <AlertCircle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">{t('esim.paymentCancelled')}</h2>
            <button
              type="button"
              onClick={() => navigate('/films')}
              className="mt-4 px-6 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm"
              data-testid="esim-cancelled-back-btn"
            >
              {t('esim.close')}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default EsimSuccessPage;
